import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicAPI } from "../services/api";
import {
	Room,
	RoomEvent,
	RemoteParticipant,
	RemoteTrack,
	RemoteTrackPublication,
	Track,
	DisconnectReason,
	Participant,
} from "livekit-client";
import { useToast } from "../components/ui/toast";
import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
	Video,
	Volume2,
	VolumeX,
	Wifi,
	WifiOff,
	Radio,
	Maximize2,
	Eye,
	Mic,
} from "lucide-react";
import { cn } from "../lib/utils";

interface StreamInfo {
	id: string;
	title: string;
	description?: string;
	type: "video" | "audio" | "video_audio";
	status: "created" | "live" | "ended";
	current_viewers: number;
	max_viewers: number;
	is_recording_enabled: boolean;
	is_chat_enabled: boolean;
	user: {
		id: string;
		first_name: string;
		last_name: string;
	};
}

const PublicStreamViewer: React.FC = () => {
	const { streamId } = useParams<{ streamId: string }>();
	const [searchParams] = useSearchParams();
	const autoConnect = searchParams.get('auto_connect') !== '0'; // Default to true (auto-connect) unless explicitly set to '0'
	const { addToast } = useToast();

	const [streamInfo, setStreamInfo] = useState<StreamInfo | null>(null);
	const [isLoadingStream, setIsLoadingStream] = useState(true);
	const [room, setRoom] = useState<Room | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const [audioEnabled, setAudioEnabled] = useState(true);
	const [viewerCount, setViewerCount] = useState(0);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [hasVideo, setHasVideo] = useState(false);
	const [hasAudio, setHasAudio] = useState(false);
	const [connectionQuality] = useState<
		"excellent" | "good" | "poor"
	>("excellent");
	const [hasManuallyDisconnected, setHasManuallyDisconnected] = useState(false);
	const [streamEndedByPublisher, setStreamEndedByPublisher] = useState(false);

	const videoElementRef = useRef<HTMLVideoElement>(null);
	const audioElementRef = useRef<HTMLAudioElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const fetchStreamInfo = useCallback(async () => {
		try {
			setIsLoadingStream(true);

			// Use the public API to get stream info
			const response = await publicAPI.getPublicStreamInfo(streamId!);
			const data = response.data;

			if (!data.success) {
				throw new Error(data.message);
			}

			const stream = data.data.livestream;
			setStreamInfo(stream);
			setViewerCount(stream.current_viewers);

			if (stream.status === "ended") {
				addToast({
					title: "Stream Ended",
					description: "This live stream has ended.",
					variant: "default",
				});
				return;
			}


		} catch (error) {
			console.error("Failed to fetch stream info:", error);
			if (error && typeof error === 'object' && 'response' in error) {
				const axiosError = error as any;
				addToast({
					title: "Stream Not Found",
					description: axiosError.response?.data?.message || "Stream not found",
					variant: "destructive",
				});
			} else {
				addToast({
					title: "Stream Not Found",
					description: "This live stream is not available or does not exist.",
					variant: "destructive",
				});
			}
		} finally {
			setIsLoadingStream(false);
		}
	}, [streamId, addToast]);

	useEffect(() => {
		if (streamId) {
			fetchStreamInfo();
		}

		return () => {
			if (room) {
				room.disconnect();
			}
		};
	}, [streamId, fetchStreamInfo, room]);

	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		return () =>
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
	}, []);

	const handleTrackSubscribed = useCallback((
		track: RemoteTrack,
		participant: Participant,
	) => {
		if (track.kind === Track.Kind.Video) {
			setHasVideo(true);
			if (videoElementRef.current) {
				track.attach(videoElementRef.current);
			}
		} else if (track.kind === Track.Kind.Audio) {
			setHasAudio(true);
			if (audioElementRef.current) {
				track.attach(audioElementRef.current);
			}
		}
	}, []);

	const handleTrackUnsubscribed = useCallback((track: RemoteTrack) => {
		if (track.kind === Track.Kind.Video) {
			setHasVideo(false);
			track.detach();
		} else if (track.kind === Track.Kind.Audio) {
			setHasAudio(false);
			track.detach();
		}
	}, []);

	const updateViewerCount = useCallback((currentRoom: Room) => {
		const viewers = Array.from(currentRoom.remoteParticipants.values()).filter(
			p => p.identity.startsWith('anonymous_viewer_') || p.identity.startsWith('viewer_')
		);
		setViewerCount(viewers.length);
	}, []);

	const handleParticipantConnected = useCallback((participant: RemoteParticipant) => {
		// Subscribe to existing tracks
		participant.trackPublications.forEach((publication) => {
			if (publication.track) {
				handleTrackSubscribed(publication.track, participant);
			}
		});
	}, [handleTrackSubscribed]);

	const joinStream = useCallback(async () => {
		if (!streamId || !streamInfo) return;

		setIsConnecting(true);
		setHasManuallyDisconnected(false); // Reset manual disconnect flag when joining

		try {
			// Generate anonymous viewer token using public API
			const tokenResponse = await publicAPI.generatePublicViewerToken(streamId, {
				identity: `anonymous_viewer_${Date.now()}`
			});

			const tokenResult = tokenResponse.data;
			if (!tokenResult.success) {
				throw new Error(tokenResult.message);
			}

			// Create room instance
			const newRoom = new Room();
			setRoom(newRoom);

			// Set up event listeners
			newRoom.on(RoomEvent.Connected, () => {
				console.log("Connected to stream");
				setIsConnected(true);
				setIsConnecting(false);
			});

			newRoom.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
				console.log("Disconnected from stream:", reason);
				setIsConnected(false);
				setRoom(null);
				setHasVideo(false);
				setHasAudio(false);

				if (reason !== DisconnectReason.CLIENT_INITIATED) {
					addToast({
						title: "Connection Lost",
						description: "You have been disconnected from the stream.",
						variant: "destructive",
					});
				}
			});

			newRoom.on(
				RoomEvent.ParticipantConnected,
				(participant: RemoteParticipant) => {
					console.log("Participant connected:", participant.identity);
					handleParticipantConnected(participant);
					updateViewerCount(newRoom);
				},
			);

			newRoom.on(
				RoomEvent.ParticipantDisconnected,
				(participant: RemoteParticipant) => {
					console.log("Participant disconnected:", participant.identity);
					if (participant.identity.startsWith("publisher_")) {
						// Publisher has left - mark stream as ended
						setStreamEndedByPublisher(true);
						addToast({
							title: "Stream Ended",
							description: "The host has ended the stream.",
							variant: "default",
						});
					}
					updateViewerCount(newRoom);
				},
			);

			newRoom.on(
				RoomEvent.TrackSubscribed,
				(
					track: RemoteTrack,
					publication: RemoteTrackPublication,
					participant: RemoteParticipant,
				) => {
					console.log("Track subscribed:", track.kind);
					handleTrackSubscribed(track, participant);
				},
			);

			newRoom.on(
				RoomEvent.TrackUnsubscribed,
				(
					track: RemoteTrack,
					publication: RemoteTrackPublication,
					participant: RemoteParticipant,
				) => {
					console.log("Track unsubscribed:", track.kind);
					handleTrackUnsubscribed(track);
				},
			);

			// Connect to the room
			await newRoom.connect(
				process.env.REACT_APP_LIVEKIT_WS_URL!,
				tokenResult.data.token,
			);

			// Handle existing participants
			newRoom.remoteParticipants.forEach((participant) => {
				handleParticipantConnected(participant);
			});

			// Update initial viewer count
			updateViewerCount(newRoom);
		} catch (error) {
			console.error("Failed to join stream:", error);
			// Only show toast if not in auto-reconnect loop
			if (!autoConnect || hasManuallyDisconnected) {
				addToast({
					title: "Join Failed",
					description: "Failed to join the stream. Please try again.",
					variant: "destructive",
				});
			}
			setIsConnecting(false);
		}
	}, [streamId, streamInfo, addToast, autoConnect, hasManuallyDisconnected, updateViewerCount, handleParticipantConnected, handleTrackSubscribed, handleTrackUnsubscribed]);

	// Auto-connect effect
	useEffect(() => {
		if (autoConnect && streamInfo && streamInfo.status === 'live' && !isConnected && !isConnecting && !isLoadingStream && !hasManuallyDisconnected && !streamEndedByPublisher) {
			joinStream();
		}
	}, [autoConnect, streamInfo, isConnected, isConnecting, isLoadingStream, hasManuallyDisconnected, streamEndedByPublisher, joinStream]);







	const leaveStream = () => {
		// Set manual disconnect flag to prevent auto-reconnect
		setHasManuallyDisconnected(true);

		if (room) {
			room.disconnect();
		}
	};

	const toggleAudio = () => {
		setAudioEnabled(!audioEnabled);
		if (audioElementRef.current) {
			audioElementRef.current.muted = audioEnabled;
		}
	};

	const toggleFullscreen = async () => {
		if (!containerRef.current) return;

		try {
			if (!isFullscreen) {
				await containerRef.current.requestFullscreen();
			} else {
				await document.exitFullscreen();
			}
		} catch (error) {
			console.error("Fullscreen error:", error);
		}
	};



	const getConnectionIcon = () => {
		if (!isConnected) return <WifiOff className="h-4 w-4 text-red-500" />;

		switch (connectionQuality) {
			case "excellent":
				return <Wifi className="h-4 w-4 text-green-500" />;
			case "good":
				return <Wifi className="h-4 w-4 text-yellow-500" />;
			case "poor":
				return <Wifi className="h-4 w-4 text-red-500" />;
			default:
				return <Wifi className="h-4 w-4 text-gray-500" />;
		}
	};

	if (isLoadingStream) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<div className="text-center space-y-4">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
					<p className="text-muted-foreground">Loading stream information...</p>
				</div>
			</div>
		);
	}

	if (!streamInfo) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<div className="text-center space-y-4">
					<Video className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
					<div>
						<h2 className="text-2xl font-semibold mb-2">Stream Not Available</h2>
						<p className="text-muted-foreground mb-4">
							This live stream is not available or does not exist.
						</p>
						<Button onClick={() => window.location.href = '/'}>
							Go to Homepage
						</Button>
					</div>
				</div>
			</div>
		);
	}

	if (streamEndedByPublisher) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<CardTitle>Stream Ended</CardTitle>
						<CardDescription>
							The host has ended the livestream.
						</CardDescription>
					</CardHeader>
					<CardContent className="text-center">
						<div className="flex items-center justify-center mb-4">
							<Badge variant="secondary" className="text-base px-4 py-2">
								<Radio className="h-4 w-4 mr-2" />
								Offline
							</Badge>
						</div>
						<p className="text-sm text-muted-foreground">
							Thank you for watching.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div ref={containerRef} className="h-screen w-full bg-black relative overflow-hidden">
			{/* Video Element */}
			<video
				ref={videoElementRef}
				autoPlay
				playsInline
				className="w-full h-full object-cover"
			/>

			{/* Audio Element */}
			<audio ref={audioElementRef} autoPlay muted={!audioEnabled} />

			{/* Connection Status - Top Left */}
			<div className="absolute top-4 left-4">
				<div className="flex items-center gap-2 text-white">
					{getConnectionIcon()}
					<span className="text-sm font-medium">
						{isConnected ? "Connected" : "Disconnected"}
					</span>
				</div>
			</div>

			{/* Viewer Count - Top Right */}
			<div className="absolute top-4 right-4">
				<div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 text-white flex items-center gap-2">
					<Eye className="h-4 w-4" />
					<span className="text-sm font-medium">{viewerCount}</span>
				</div>
			</div>

			{/* Not Connected Overlay */}
			{!isConnected && !streamEndedByPublisher && (
				<div className="absolute inset-0 flex items-center justify-center bg-black">
					<div className="text-center text-white">
						<WifiOff className="h-12 w-12 mx-auto mb-2 opacity-50" />
						<p className="mb-4">Not Connected</p>
						{streamInfo.status === 'live' && (
							<Button
								onClick={joinStream}
								disabled={isConnecting}
								className="bg-green-600 hover:bg-green-700 text-white px-8"
							>
								{isConnecting ? "Joining..." : "Join Stream"}
							</Button>
						)}
					</div>
				</div>
			)}

			{/* No Signal */}
			{(!hasVideo && !hasAudio && isConnected && !streamEndedByPublisher) && (
				<div className="absolute inset-0 flex items-center justify-center bg-black">
					<div className="text-center text-white">
						<Radio className="h-12 w-12 mx-auto mb-2 opacity-50" />
						<p>Waiting for Stream</p>
					</div>
				</div>
			)}

			{/* Audio Only Message */}
			{(!hasVideo && hasAudio && isConnected && !streamEndedByPublisher) && (
				<div className="absolute inset-0 flex items-center justify-center bg-black">
					<div className="text-center text-white">
						<Mic className="h-12 w-12 mx-auto mb-2 opacity-50" />
						<p>Audio Only Stream</p>
						{streamInfo && streamInfo.type === 'audio' && (
							<p className="text-sm text-gray-400 mt-2">This stream is audio only</p>
						)}
					</div>
				</div>
			)}

			{/* Bottom Controls */}
			{isConnected && !streamEndedByPublisher && (
				<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
					<div className="flex items-center gap-3">
						{/* Audio Control - Only show for audio or video_audio streams */}
						{streamInfo && (streamInfo.type === 'audio' || streamInfo.type === 'video_audio') && (
							<Button
								size="sm"
								variant="ghost"
								onClick={toggleAudio}
								className={cn(
									"w-10 h-10 rounded-full text-white hover:bg-white/20",
									audioEnabled ? "bg-white/10" : "bg-red-600"
								)}
							>
								{audioEnabled ? (
									<Volume2 className="h-4 w-4" />
								) : (
									<VolumeX className="h-4 w-4" />
								)}
							</Button>
						)}

						<Button
							size="sm"
							variant="ghost"
							onClick={toggleFullscreen}
							className="w-10 h-10 rounded-full text-white hover:bg-white/20 bg-white/10"
						>
							<Maximize2 className="h-4 w-4" />
						</Button>

						<Button
							size="lg"
							onClick={leaveStream}
							className="bg-red-600 hover:bg-red-700 text-white px-8"
						>
							Leave Stream
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default PublicStreamViewer;
